import { CommonModule } from '@angular/common'
import { ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, Renderer2, inject, input, output } from '@angular/core'
import { FormControl, ReactiveFormsModule } from '@angular/forms'
import { TranslateModule } from '@ngx-translate/core'
import { Subject, takeUntil } from 'rxjs'
import { SafeDomPipe } from '../../pipes/safe-dom.pipe'
import { ModalService } from '../../services/modal.service'

/**
 * ModalComponent - A component that represents a modal window
 *
 * @selector modal
 * @animations modalAnimation
 */
@Component({
  selector: 'modal',
  templateUrl: './modal.component.html',
  animations: [ModalService.animations],
  standalone: true,
  imports: [CommonModule, SafeDomPipe, ReactiveFormsModule, TranslateModule]
})
export class ModalComponent implements OnInit, OnDestroy {
  /**
   * @property {string} header - The header of the modal window
   */
  header = input<string>('')

  /**
   * @property {string} id - The identifier of the modal
   */
  id = input<string>('')

  /**
   * @property {boolean} closeButtonEnabled - A flag indicating whether the close button is enabled
   * @default true
   */
  closeButtonEnabled = input<boolean>(true)

  /**
   * @property {boolean} showHeaderDivider - A flag indicating whether a divider line should be shown under the header
   * @default true
   */
  showHeaderDivider = input<boolean>(true)

  /**
   * @property {boolean} showFooterDivider - A flag indicating whether a divider line should be shown above the footer
   * @default true
   */
  showFooterDivider = input<boolean>(true)

  /**
   * @property {number} width - The width of the modal window
   */
  widthClass = input<string>('')

  /**
   * @property {string} headerIcon - icon to be displayed in the header
   */
  headerIcon = input<string>('')

  /**
   * @property {boolean} headerSwitch - toggle the switch for header
   */
  headerSwitch = input<boolean>(false)

  /**
   * @property {number} height - The height of the modal window
   */
  heightClass = input<string>('')

  /**
   * @property {string} bodyClass - Optional classes for the modal body slot wrapper
   */
  bodyClass = input<string>('')

  /**
   * @property {boolean} enableBackdropClose - A flag indicating whether the modal should be closed when the user clicks outside of it
   */
  enableBackdropClose = input<boolean>(true)

  /**
   * When false, Escape does not close the modal (e.g. while a submit is in flight).
   */
  escapeToClose = input<boolean>(true)

  /**
   * @property {FormControl} switchControl - The form control for the switch
   */
  switchControl = input<FormControl>(new FormControl())
  roundedClass = input<string>('tw-rounded-xl')

  /**
   * @event onCloseModal - An event that gets emitted when the modal is closed
   */
  onCloseModal = output<void>()
  /**
   * @event onSwitchChange - An event that gets emitted when the switch is toggled
   */
  onSwitchChange = output<boolean>()
  /**
   * @property {boolean} showHeader - A flag indicating whether the header should be shown
   */
  showHeader = input<boolean>(true)

  private _destroy$ = new Subject<void>()
  private _portalOriginalParent: HTMLElement | null = null
  private readonly _changeDetectorRef = inject(ChangeDetectorRef)

  /**
   * Constructor to inject necessary services and dependencies
   * @param _ModalService - A service for managing modals
   * @param renderer - Renderer2 for DOM manipulation
   * @param elementRef - Reference to the DOM element of the component
   */
  constructor(private _ModalService: ModalService, private _renderer: Renderer2, private _elementRef: ElementRef) {}

  /**
   * Lifecycle hook: Called once the component is initialized
   * Subscribes to modal state: when open, moves the host under `document.body` so stacking works;
   * when closed, moves it back so idle instances are not left on `body`.
   */
  ngOnInit(): void {
    const host = this._elementRef.nativeElement as HTMLElement
    this._portalOriginalParent = host.parentElement

    this._ModalService.modalState$.pipe(takeUntil(this._destroy$)).subscribe((state) => {
      const modalId = this.id()
      if (!modalId) return

      const isOpen = !!state[modalId]

      if (isOpen) {
        if (host.parentElement !== document.body) this._renderer.appendChild(document.body, host)
      } else if (host.parentElement === document.body && this._portalOriginalParent) this._renderer.appendChild(this._portalOriginalParent, host)

      if (this._ModalService.hasOpenModal()) this._renderer.addClass(document.body, 'tw-overflow-hidden')
      else this._renderer.removeClass(document.body, 'tw-overflow-hidden')

      this._changeDetectorRef.markForCheck()
    })
  }

  /**
   * Lifecycle hook: Called once the component is destroyed
   * Removes the modal component from the <body> tag to clean up the DOM
   */
  ngOnDestroy(): void {
    // Complete the destroy subject to clean up subscriptions
    this._destroy$.next()
    this._destroy$.complete()

    const host = this._elementRef.nativeElement as HTMLElement
    if (host.parentElement === document.body && this._portalOriginalParent) this._renderer.appendChild(this._portalOriginalParent, host)
    if (!this._ModalService.hasOpenModal()) this._renderer.removeClass(document.body, 'tw-overflow-hidden')
  }

  /**
   * Host listener for escape key press to close the modal
   * @param event - Keyboard event
   */
  @HostListener('document:keydown.escape', ['$event'])
  protected _onKeydownHandler(event: KeyboardEvent) {
    if (!this.isModalOpen() || !this.escapeToClose()) return
    // Stop this keydown so the wizard (or other overlays) does not also close in the same press.
    event.preventDefault()
    event.stopImmediatePropagation()
    this.closeModal()
  }

  /**
   * Check if the modal window is open
   *
   * @returns {boolean} - returns true if the modal window with the given id is open, otherwise false
   */
  isModalOpen(): boolean {
    return this._ModalService.isModalOpen(this.id())
  }

  /**
   * Handle backdrop click to close modal
   */
  onBackdropClick(event: Event) {
    if (!this.enableBackdropClose()) return
    this.closeModal()
  }

  /**
   * Handle modal content click to prevent closing
   */
  onModalContentClick(event: Event) {
    event.stopPropagation()
  }

  /**
   * Emit an onCloseModal event
   */
  closeModal() {
    this._ModalService.toggleModal(this.id())
    this.onCloseModal.emit()
  }

  /**
   * @method switchChange
   * @description Handle the click event on the switch
   * @param event - The event object
   * @returns void
   */
  public switchChange(event: Event) {
    const checked = (event.target as HTMLInputElement).checked
    this.onSwitchChange.emit(checked)
  }
}
